import Highcharts from 'highcharts';
import BrokenAxis from 'highcharts/modules/broken-axis';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import BaseHighchartsDataview from './BaseHighchartsDataview';
import { getPeriodOverPeriodTooltip, getSharedTooltip, sharedTooltipPositioner } from './tooltipRenderers';

BrokenAxis(Highcharts);

@inject('$app', '$dictionary')
@observer
export default class StackedColumnView extends BaseHighchartsDataview {
  chartOptions = {
    chart: {
      zooming: { type: 'x' },
      events: {
        selection: this.handleTimeZoom
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      type: 'datetime',
      minRange: 60000,
      dateTimeLabelFormats: {
        day: '%m/%e',
        minute: '%H:%M'
      },
      title: {
        text: 'UTC'
      },
      crosshair: true
    },
    yAxis: {
      min: 0.001,
      title: {
        text: 'units go here'
      }
    },
    plotOptions: {
      series: {
        marker: {
          enabled: false
        }
      },
      column: {
        stacking: 'normal',
        groupPadding: 0,
        pointPadding: 0.05
      }
    },
    legend: { enabled: false },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      followPointer: true,
      hideDelay: 0,
      positioner: sharedTooltipPositioner.bind(this)
    },
    series: []
  };

  renderSeries(bucket, name, data) {
    if (!this.chart) {
      return null;
    }

    if (data.length) {
      const breaks = [];
      const origDuration = data[0][2] * 1000;
      for (let x = 0, xlen = data.length - 1; x < xlen; x += 1) {
        if (data[x + 1][0] - data[x][0] > origDuration) {
          breaks.push({
            from: data[x][0] + data[x][2] * 1000,
            to: data[x + 1][0]
          });
        } else if (data[x + 1][0] - data[x][0] < origDuration) {
          data.splice(x, 1);
          x -= 1;
          xlen -= 1;
        }
      }

      this.chart.xAxis[0].update({ breaks }, false);
    }

    const isPreviousPeriod = bucket.get('isPreviousPeriod');
    let type = 'column';
    let className = '';
    let visible = true;

    if (isPreviousPeriod) {
      className = 'highcharts-series-dash';

      if (name === 'Total') {
        type = 'line';
      } else {
        visible = false;
      }
    }

    const seriesData = { type, name, data, isPreviousPeriod, className, visible };
    const matchingSeries = this.chart.series.find((series) => series.name === name);
    if (matchingSeries) {
      seriesData.linkedTo = name;
      seriesData.color = matchingSeries.color;
      this.addNativeLegendHoverEvents(matchingSeries);
    } else {
      seriesData.id = name;
    }
    return this.chart.addSeries(seriesData, false);
  }

  findSeries(bucket, name) {
    const isNegative = bucket.get('name') === 'Left -Y Axis';
    return (
      this.chart &&
      this.chart.series.find(
        (series) => series.name === name && ((isNegative && series.dataMax < 0) || series.dataMax > 0)
      )
    );
  }

  removeSeries(bucketName, name) {
    const isNegative = bucketName === 'Left -Y Axis';
    const existingSeries =
      this.chart &&
      this.chart.series.find(
        (series) => series.name === name && ((isNegative && series.dataMax < 0) || series.dataMax > 0)
      );
    if (existingSeries) {
      existingSeries.remove(false);
    } else {
      console.warn('Attempted to remove series', name, 'from bucket', bucketName, ', but no series was found.');
    }
  }

  renderOverlay(bucket, name, data) {
    if (!this.chart) {
      return null;
    }

    const type = 'line';
    const isPreviousPeriod = bucket.get('isPreviousPeriod');
    let colorIndex = 50;
    let className = '';

    if (name.startsWith('Historical Total')) {
      colorIndex = 500;
    }

    if (isPreviousPeriod) {
      className = 'highcharts-series-dash';
    }

    for (let x = 0, xlen = data.length - 1, origDuration = data[0][2] * 1000; x < xlen; x += 1) {
      if (data[x + 1][0] - data[x][0] < origDuration) {
        data.splice(x, 1);
        x -= 1;
        xlen -= 1;
      }
    }

    const seriesData = { type, name, data, colorIndex, isPreviousPeriod, className, zIndex: 1 };
    const matchingSeries = this.chart.series.find((series) => series.name === name);
    if (matchingSeries) {
      seriesData.linkedTo = name;
      this.addNativeLegendHoverEvents(matchingSeries);
    } else {
      seriesData.id = name;
    }

    return this.chart.addSeries(seriesData, false);
  }

  redraw(opts) {
    const { $app } = this.props;
    $app.renderSync(() => {
      if (this.chart) {
        if (this.chart.series.length > 0) {
          const { dateRangeDisplay } = this.props.dataview.queryBuckets.activeBuckets[0].firstQuery;
          const { data } = this.chart.series[0].userOptions;
          const minuteIntervals = (data[1][0] - data[0][0]) / 60000;
          const dateRangeIntervalsDisplay = `${
            minuteIntervals >= 1 ? `${minuteIntervals} minute intervals` : `${minuteIntervals * 60} second intervals`
          }`;
          this.chart.xAxis[0].setTitle({
            text: `${dateRangeDisplay} (${dateRangeIntervalsDisplay})`
          });
        }

        const [axis] = this.chart.yAxis;
        const { dataMin, dataMax } = axis.getExtremes();
        const extreme = Math.max(Math.abs(dataMin), dataMax);
        if (extreme) {
          axis.setExtremes(dataMin < 0 ? extreme * -1 : 0.001, extreme, false);
        }
      }
    });

    super.redraw(opts);
  }

  getComponent() {
    const { chartOptions } = this;
    const { $dictionary, dataview, showNativeLegend } = this.props;
    const { activeBucketCount, activeBuckets } = dataview.queryBuckets;

    if (!activeBucketCount) {
      return null;
    }

    const query = activeBuckets[0].firstQuery;

    if (query.get('outsort').includes('agg_total')) {
      throw new Error('This Time-Series chart does not support Display & Sort By Total.');
    }

    if (activeBucketCount > 1) {
      chartOptions.yAxis.min = null;
    }

    chartOptions.xAxis.momentFn = query.get('time_format') === 'UTC' ? moment.utc : moment;
    chartOptions.xAxis.title.text = query.dateRangeDisplay;
    chartOptions.yAxis.title.text = $dictionary.get('units')[query.outsortUnit];
    chartOptions.yAxis.unit = query.outsortUnit;
    chartOptions.yAxis.type = query.get('use_log_axis') ? 'logarithmic' : 'linear';
    chartOptions.yAxis.custom = query.get('use_log_axis') ? { allowNegativeLog: true } : {};

    const reAggInterval = query.get('reAggInterval');
    chartOptions.plotOptions.column.pointRange = typeof reAggInterval === 'number' ? reAggInterval * 1000 : undefined;

    if (showNativeLegend) {
      chartOptions.legend = {
        itemStyle: {
          color: `${this.chartLabelColor} !important`,
          fontWeight: 'normal'
        },
        itemHoverStyle: {
          color: `${this.chartLabelColor} !important`,
          fontWeight: 'bold !important'
        },
        labelFormatter() {
          return this.name.substr(0, 16);
        },
        verticalAlign: 'bottom',
        align: 'center'
      };
    }

    const { getColorFromIndex } = this;
    if (dataview.queryBuckets.hasPeriodOverPeriod) {
      chartOptions.tooltip.formatter = function formatter() {
        const points = (this.points || []).map((p) => p.point);
        return getPeriodOverPeriodTooltip({
          points,
          offset: moment
            .duration(query.get('period_over_period_lookback'), query.get('period_over_period_lookback_unit'))
            .asMilliseconds(),
          getColorFromIndex,
          showSeconds: query.get('reAggInterval') ? query.get('reAggInterval') < 60 : false
        });
      };
    } else {
      chartOptions.tooltip.formatter = function formatter() {
        const points = (this.points || []).map((p) => p.point);
        return getSharedTooltip({
          points,
          seriesType: 'column',
          tooltip: getSharedTooltip({
            points: this.points,
            getColorFromIndex,
            showSeconds: query.get('reAggInterval') ? query.get('reAggInterval') < 60 : false
          }),
          getColorFromIndex
        });
      };
    }

    return super.getComponent();
  }
}

const config = {
  showTotalTrafficOverlay: true,
  showLegend: true,
  timeBased: true,
  isSVG: true,
  enableToggle: true,
  mirrorable: true,
  supportsLogAxis: true,
  buckets: [
    {
      name: 'Left +Y Axis',
      mirrorBucket: 1
    },
    {
      name: 'Left -Y Axis',
      sampleRateFactor: -1
    }
  ]
};

export { config };
